import React, { useEffect, useState } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Slide } from '@wds/motion';
import { Checkbox } from '@wds/checkbox';
import { FormField, FormGroup } from '@wds/form-group';
import { Radio } from '@wds/radio';

import { getCookie, isSafeRedirectUrl } from 'sma.common/utils';

import PropTypes from 'prop-types';
import { ProgressCircle } from '@wds/progress-circle';
import STRINGS from '../constants';
import MFAErrorMessage from './MFAErrorMessage';

const Challenge = (props) => {
	const {
		currentPage,
		setCurrentPage,
		isLoaded,
		context,
		setContext,
		error,
		setError,
		setMessage,
		isSliding,
		setIsSliding,
		handleError,
		nextUrl,
		method,
		message,
		isChange,
		sending,
		cooldown,
		sendEmailCode,
		onOpenRecoveryModal,
	} = props;

	const [isValid, setIsValid] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [resending, setResending] = useState(false);
	const [newMethod, setNewMethod] = useState('totp');
	const [hideChallenge, setHideChallenge] = useState(false);

	useEffect(() => {
		// Note: mfaLoginPrompted is now tracked on backend in MFAView
		// This ensures we have proper user_id instead of "Anonymous User"
	}, []);

	useEffect(() => {
		if (context.challengeCode.match(/^[0-9]+$/) && context.challengeCode.length === 6) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}
		if (isChange && context.changeInProgress) {
			setHideChallenge(true);
			setIsValid(true);
		}
	}, [context]);

	const onSelectNewMethod = (e) => {
		setNewMethod(e.target.value);
	};

	const onHandleContextChange = (key) => (e) => {
		let { value } = e.target;
		if (key === 'challengeCode') {
			value = value.replace(/\D/g, '').slice(0, 6);
		}
		setContext({ ...context, [key]: value });
	};

	const handleRecoverAccountClick = (e) => {
		e.preventDefault();
		if (window.track) {
			window.track('mfaRecoveryAccountInitiated', {
				currentMethod: method,
				newMethod: newMethod,
				context: 'device_change'
			});
		}
		if (onOpenRecoveryModal) {
			onOpenRecoveryModal(newMethod);
		}
	};

	const submitChallengeCode = async () => {
		setError('');
		setMessage('');
		setIsSubmitting(true);
		let isNavigating = false;
		try {
			let url;
			let action;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'begin_device_change';
			} else {
				url = reverse('api-internal-mfa-challenge');
				action = method === 'totp' ? 'verify_totp_challenge' : 'verify_email_challenge';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({
					action,
					method: isChange ? newMethod : method,
					challenge_code: context.challengeCode,
					remember_device: context.rememberDevice,
				}),
			});
			const data = await response.json();
			if (isChange) {
				if (data.success) {
					setError('');
					setMessage('');
					let newContext = { ...context };
					if (data.method.method === 'totp') {
						newContext = {
							...newContext,
							challengeCode: '',
							new_method: newMethod,
							method_id: data.method.id,
							qrCodeString: data.qr_code,
							securityKeyString: data.secret,
							changeInProgress: true,
						};
					} else if (data.method.method === 'email') {
						const emailAlreadyPending = data.message === 'email_code_pending';
						newContext = {
							...newContext,
							challengeCode: '',
							new_method: newMethod,
							method_id: data.method.id,
							status: emailAlreadyPending ? 'code_already_pending' : 'code_pending',
							cooldown_remaining: data.time_remaining || 0,
							changeInProgress: true,
						};
					}
					setContext(newContext);
					if (data.method.method === 'totp') {
						setCurrentPage('totp-enrollment');
					} else if (data.method.method === 'email') {
						setCurrentPage('email-enrollment');
					}
				}
				if (data.error) {
					if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
						isNavigating = true;
						window.location.href = reverse('mfa-lockout');
						return
					}
					if (data.error === 'code_already_pending') {
						// Set the proper status so EmailEnrollment shows correct UI
						setContext({
							...context,
							challengeCode: '',
							new_method: newMethod,
							method_id: data.method?.id,
							status: 'code_already_pending',
							cooldown_remaining: data.time_remaining || 0,
							changeInProgress: true,
						});
						setCurrentPage('email-enrollment');
						return;
					}
					setError(data.error);
				}
			}
			if (!isChange) {
				if (data.success) {
					setError('');
					// Check if user needs to set up recovery email
					if (data.needs_recovery_email) {
						setContext({
							...context,
							needsRecoveryEmail: true,
							challengeCode: '',
						});
						setCurrentPage('recovery-email-setup');
					} else if (data.next_url) {
						isNavigating = true;
						window.location.replace(data.next_url);
					} else if (isSafeRedirectUrl(nextUrl)) {
						isNavigating = true;
						window.location.replace(nextUrl);
					} else {
						isNavigating = true;
						window.location.replace('/');
					}
				} else if (data.error) {
					if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
						isNavigating = true;
						window.location.href = reverse('mfa-lockout');
						return
					} else {
						setError(data.error);
					}
				}
			}
		} catch (err) {
			handleError(`Error submitting challenge code: ${err}`);
		} finally {
			if (!isNavigating) {
				setIsSubmitting(false);
			}
		}
	};

	const handleResendEmailCode = async () => {
		setResending(true);
		await sendEmailCode();
		setResending(false);
	};

	const inputColor = error ? 'error' : '';

	const handleCheckboxChange = (event) => {
		// Note: Remember device state is tracked in mfaLoginSuccess event's 'remembered' parameter
		// Tracking the toggle here would show "Anonymous User" since login isn't complete yet
		setContext({ ...context, rememberDevice: event.target.checked });
	};

	return (
		<Slide
			in={isLoaded && currentPage === 'challenge'}
			speed={currentPage === 'challenge' ? 'slow' : undefined}
			timeout={currentPage === 'challenge' ? undefined : 150}
			direction='right'
			onEnter={() => setIsSliding(true)}
			onExited={() => setIsSliding(false)}
			alwaysRenderContent
		>
			<Box
				display='flex'
				flexDirection='column'
				gap={4}
				style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}
			>
				{sending && !resending && (
					<Box
						display='flex'
						flexDirection='column'
						justifyContent='center'
						alignItems='center'
						gap={2}
						mt={9}
					>
						<Box display='flex' justifyContent='center' alignItems='center'>
							<ProgressCircle continuous />
						</Box>
					</Box>
				)}
				{hideChallenge && (
					<Box
						p={4}
						style={{
							backgroundColor: '#42ac14',
							borderRadius: '5px',
							color: '#FFFFFF',
						}}
						role='alert'
						aria-live='assertive'
					>
						{STRINGS.changeInProgress}
					</Box>
				)}
				{(!sending || resending) && (
					<>
						<Box
							display='flex'
							flexDirection='column'
							justifyContent='center'
							alignItems='center'
							gap={2}
						>
							{isChange && (
								method !== 'email' ? (
									<FormGroup>
										<FormField>
											<Typography variant='bodySm' align='center'>
												{STRINGS.whatMethod}
											</Typography>
										</FormField>
										<FormField>
											<Radio
												label={STRINGS.authenticatorAppTOTP}
												onChange={onSelectNewMethod}
												name='mfaMethodSelect'
												value='totp'
												checked={newMethod === 'totp'}
											/>
										</FormField>
										<FormField>
											<Radio
												label={STRINGS.email}
												onChange={onSelectNewMethod}
												name='mfaMethodSelect'
												value='email'
												checked={newMethod === 'email'}
											/>
										</FormField>
									</FormGroup>
								) : (
									<Typography variant='bodySm' align='center'>
										{STRINGS.totpOnly}
									</Typography>
								)
							)}
							{!hideChallenge && (
								<>
									<Typography variant='sectionTitleSm' align='center' weight='medium'>
										{isChange ? (method === 'email' ? STRINGS.enterOneTimeCodeToContinue : STRINGS.enterCodeToContinue) : STRINGS.enterCodeToLogin}
									</Typography>
									{!isChange && method === 'email' && context.masked_email && (
										<Typography variant='bodySm' align='center'>
											{STRINGS.recoveryEmailChallengeDescription.replace('{masked_email}', context.masked_email)}
										</Typography>
									)}
									{!isChange && method === 'email' && !context.masked_email && (
										<Typography variant='bodySm' align='center'>
											{STRINGS.checkEmail}
										</Typography>
									)}
									{isChange && method === 'totp' && (
										<Typography variant='bodySm' align='center'>
											{STRINGS.beforeChangingDevice}
										</Typography>
									)}
									{isChange && method === 'email' && (
										<Typography variant='bodySm' align='center'>
											{STRINGS.beforeChangingDeviceEmail}
										</Typography>
									)}
									{!isChange && method === 'totp' && (
										<Typography variant='bodySm' align='center'>
											{STRINGS.checkApp}
										</Typography>
									)}
								{method === 'email' && (
									<Button
										onClick={handleResendEmailCode}
										id='start-resend-email-button'
										size='lg'
										aria-label={STRINGS.resendCode}
										disabled={cooldown > 0 || resending}
									>
										{resending ? (
											<ProgressCircle size='sm' continuous />
										) : cooldown > 0
											? `${STRINGS.resendCode} (${cooldown}s)`
											: (isChange ? STRINGS.sendCode : STRINGS.resendCode)}
									</Button>
								)}
								</>
							)}
						</Box>
						<Box
							display='flex'
							flexDirection='column'
							gap={4}
							pb={6}
						>
							{!hideChallenge && (
								<>
										<MFAErrorMessage error={error} />
									{message && (
										<Box
											p={4}
											style={{
												backgroundColor: '#42ac14',
												borderRadius: '5px',
												color: '#FFFFFF',
											}}
											role='alert'
											aria-live='assertive'
										>
											{STRINGS?.[message] || message}
										</Box>
									)}
								<Input
									value={context.challengeCode}
									placeholder={STRINGS.enterCodeProvided}
									onChange={onHandleContextChange('challengeCode')}
									id='mfa-challenge-code'
									type='text'
									inputMode='numeric'
									maxLength={6}
									aria-required='true'
									autoFocus
									required
									style={{
										width: '100%',
										boxSizing: 'border-box',
										padding: '0 16px',
										fontSize: '15px',
										borderRadius: '4px',
										border: error ? '1px solid #DC2626' : '1px solid #D1D5DB',
										height: '48px',
										display: 'flex',
										alignItems: 'center',
									}}
								/>
									{!isChange && (
										<Box mt={4}>
											<Checkbox
												label={STRINGS.rememberDevice}
												onChange={handleCheckboxChange}
												aria-label={STRINGS.rememberDevice}
											/>
										</Box>
									)}
								{onOpenRecoveryModal && (
									<Box mt={4}>
										<Typography component='p' variant='bodySm' style={{ color: '#6B7280', marginBottom: '8px' }}>
											{STRINGS.doNotHaveAccessToDevice}
										</Typography>
										<a
											href='#'
											onClick={handleRecoverAccountClick}
											style={{ color: '#0EA5E9', textDecoration: 'none', fontSize: '14px' }}
										>
											{STRINGS.recoverAccount}
										</a>
									</Box>
								)}
								</>
							)}
							<Button
								id='next-button'
								size='lg'
								disabled={!isValid || isSubmitting}
								aria-disabled={!isValid || isSubmitting}
								onClick={submitChallengeCode}
							>
								{isSubmitting ? (
									<ProgressCircle size='sm' continuous />
								) : (method === 'email' ? STRINGS.confirm : STRINGS.continue)}
							</Button>
						</Box>
					</>
				)}
			</Box>
		</Slide>
	);
};

Challenge.propTypes = {
	isLoaded: PropTypes.bool.isRequired,
	context: PropTypes.object.isRequired,
	setContext: PropTypes.func.isRequired,
	error: PropTypes.string,
	setError: PropTypes.func.isRequired,
	isSliding: PropTypes.bool.isRequired,
	setIsSliding: PropTypes.func.isRequired,
	handleError: PropTypes.func.isRequired,
	currentPage: PropTypes.string.isRequired,
	setCurrentPage: PropTypes.func.isRequired,
	nextUrl: PropTypes.string,
	method: PropTypes.string.isRequired,
	isChange: PropTypes.bool,
	sending: PropTypes.bool,
	message: PropTypes.string.isRequired,
	setMessage: PropTypes.func.isRequired,
	cooldown: PropTypes.number.isRequired,
	sendEmailCode: PropTypes.func.isRequired,
	onOpenRecoveryModal: PropTypes.func,
};

Challenge.defaultProps = {
	error: '',
	nextUrl: '',
	isChange: false,
	sending: false,
	onOpenRecoveryModal: null,
};

export default Challenge;
